import {
  selectInfoUser,
  selectTranslations,
} from 'app/pages/App/slice/selectors';
import { I_Category, I_CheckoutCartItem } from 'app/pages/Cart/slice/types';
import {
  CheckoutDetail,
  Orderdate,
} from 'app/pages/Restaurants/Checkout/slice/types';
import { getLOVPaymentMethodReservation } from '@/app/services/commonInfoServices';
import {
  getReservationBookingDetailApi,
  paymentReservationBookingApi,
} from 'app/services/reservationHistoryApi';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { I_Park } from 'types';
import { useCheckoutSlice } from '../slice';
import { selectListPaymentMethod } from '../slice/selectors';
import { useGlobalFunction } from 'app/pages/App/hooks/useGlobalFunction';
import moment from 'moment';
import { isTimeValid } from 'utils/time';
import { openMessageWithIcon, windowOpenLink } from 'utils/helper';
import { ReservationHistory_PaymentStatus, ReservationHistoryStatus } from '@/app/pages/reservation-history/store';
import { formatDateBEtoUTCFormat } from '@/lib/utils';

export type GroupDatas = {
  park: I_Park;
  catalogs: Record<
    string,
    {
      catalog: any;
      items: I_CheckoutCartItem[];
    }
  >;
  show?: any;
};

export const MATERRIAL_SHOW_TYPE = '9004'; //Vé show
export const MATERRIAL_PARK_TYPE = '9005'; //Vé công viên
export const MATERRIAL_EVOUCHER_TYPE = '9006'; //Evoucher
export const MATERRIAL_COMBO_TYPE = '9008'; //Combo

const groupItems = (
  items: I_CheckoutCartItem[],
): Record<
  string,
  {
    park: I_Park;
    catalogs: Record<string, { catalog: any; items: I_CheckoutCartItem[] }>;
  }
> => {
  return items.reduce((groupedItems, item) => {
    const parkKey = `${item.park.id}`;
    const catalogKey = `${item.catalog.id}`;

    if (!groupedItems[parkKey]) {
      groupedItems[parkKey] = { park: item.park, catalogs: {} };
    }

    if (!groupedItems[parkKey].catalogs[catalogKey]) {
      groupedItems[parkKey].catalogs[catalogKey] = {
        catalog: item.catalog,
        items: [],
      };
    }

    groupedItems[parkKey].catalogs[catalogKey].items.push(item);

    return groupedItems;
  }, {} as Record<string, { park: I_Park; catalogs: Record<string, { catalog: any; items: I_CheckoutCartItem[] }> }>);
};

const getFirstValue = (...values: any[]) =>
  values.find(value => value !== undefined && value !== null && value !== '');

const getPaymentMethodLabel = (value: any, options: any[]) => {
  if (value === undefined || value === null || value === '') return '';

  const matchedPayment = options.find(
    item =>
      `${item?.mapVGS ?? ''}` === `${value}` ||
      `${item?.code ?? ''}` === `${value}` ||
      `${item?.id ?? ''}` === `${value}`,
  );

  return matchedPayment?.name ?? matchedPayment?.code ?? value;
};

const hasReservationDetailData = (detail: any) => {
  if (!detail) return false;
  if (Array.isArray(detail)) return detail.length > 0;
  if (typeof detail !== 'object') return true;

  return Object.keys(detail).length > 0;
};

const getDateObject = (value: any): Orderdate | undefined => {
  const rawValue =
    typeof value === 'object'
      ? getFirstValue(
        value?.datetimeDMYHIS,
        value?.datetime,
        value?.dateDMY,
        value?.date,
        value?.time,
      )
      : value;
  if (!rawValue) return undefined;

  const parsed = moment(
    rawValue,
    [
      'HH:mm:ss DD/MM/YYYY',
      'YYYY-MM-DDTHH:mm:ss.SSSZ',
      'YYYY-MM-DDTHH:mm:ssZ',
      'YYYY-MM-DD HH:mm:ss',
      'YYYY-MM-DD',
      'YYYYMMDD',
      'DD/MM/YYYY HH:mm:ss',
      'DD/MM/YYYY HH:mm',
      'DD/MM/YYYY',
    ],
    true,
  );
  const date = parsed.isValid() ? parsed : moment(rawValue);
  if (!date.isValid()) return undefined;

  return {
    time: date.format('HH:mm'),
    date: date.format('YYYY-MM-DD'),
    dateOrigin: date.format('YYYY-MM-DD'),
    datetime: date.format('YYYY-MM-DD HH:mm:ss'),
    dateDMY: date.format('DD/MM/YYYY'),
    datetimeDMY: date.format('DD/MM/YYYY HH:mm'),
    datetimeDMYHIS: date.format('HH:mm:ss DD/MM/YYYY'),
  };
};

const formatApiDate = (value: any) => {
  const date = getDateObject(value);
  return date?.date ?? value ?? '';
};

const getCheckoutErrorMessage = (error: any) => {
  const payload = error?.response?.data ?? error;
  return (
    payload?.errors?.[0]?.message ??
    payload?.data?.errors?.[0]?.message ??
    payload?.message ??
    ''
  );
};

export const mapReservationDetailToCheckout = (
  detail: any,
  reservationNo: string,
): CheckoutDetail => {
  const amount = Number(detail?.price) || 0;
  const currency = detail?.currency?.value ?? detail?.currency ?? 'VND';
  const quantity = Number(detail?.partySize) || 1;
  const reservationDate = detail?.reservationDate ?? '';
  const reservationTime = detail?.reservationTime ?? '';
  const reservationDateObject = getDateObject(reservationDate);
  const reservationDateLabel = [
    reservationDateObject?.dateDMY ?? reservationDate,
    reservationTime,
  ]
    .filter(Boolean)
    .join(', ');
  const itemId = detail?.reservationItemId ?? reservationNo;
  const orderNo = detail?.reservationNo ?? reservationNo;
  const orderDate = getDateObject(detail?.createdAt);
  const expiredAt = getDateObject(
    formatDateBEtoUTCFormat(detail?.payment?.paymentDueAt),
  );
  const emptyImage = {
    original: '',
    thumb640: '',
    thumb1000: '',
    thumb1600: '',
    thumb1920: '',
  };
  const checkoutItem: any = {
    id: itemId,
    reservationItemId: detail?.reservationItemId,
    orderId: detail?.orderId,
    variantId: detail?.variantId,
    storeId: detail?.storeId,
    zoneId: detail?.zoneId,
    siteId: detail?.siteId,
    productName: detail?.productName,
    amount: `${quantity}`,
    price: amount,
    totalPrice: amount,
    originalPrice: amount,
    productNumber: orderNo,
    productCode: detail?.reservationNo,
    tickets: [],
    product: {
      id: detail?.variantId,
      name: detail?.productName,
      price: amount,
      originalPrice: amount,
      categories: [],
      amount: `${quantity}`,
      parks: [],
      image: '',
    },
    date: reservationDateObject
      ? { ...reservationDateObject, dateDMY: reservationDateLabel }
      : { dateDMY: reservationDateLabel },
    zone: {
      id: detail?.zoneId,
      name: detail?.zoneName,
      code: '',
    },
    show: {
      id: detail?.variantId,
      name: detail?.productName,
    },
    performance: {},
    park: {
      id: detail?.storeId,
      name: detail?.storeName,
      image: emptyImage,
    },
    catalog: {
      id: detail?.siteId,
      name: detail?.siteName,
    },
    isGift: false,
    selectedDate: {
      dateDMY: reservationDateLabel,
    },
  };

  return {
    ...detail,
    id: orderNo,
    idHash: orderNo,
    ordernumber: orderNo,
    reservationId: detail?.reservationItemId,
    reservationCode: detail?.reservationNo,
    reservationNo: orderNo,
    quantity,
    infantQuantity: 0,
    representativeName: detail?.booker?.fullName ?? '',
    representativePhone: detail?.booker?.phoneNumber ?? '',
    arrivalDate: formatApiDate(detail?.reservationDate),
    storeId: detail?.storeId,
    ticketShowId: detail?.variantId,
    zoneId: detail?.zoneId,
    referentCode: '',
    orderdate: orderDate,
    name: detail?.booker?.fullName ?? '',
    phone: detail?.booker?.phoneNumber ?? '',
    email: detail?.booker?.email ?? '',
    totalDiscount: 0,
    totalPrice: amount,
    totalOriginalPrice: amount,
    currency,
    payment: {
      ...(detail?.payment ?? {}),
      id: detail?.payment?.id ?? 0,
      name: detail?.payment?.name ?? detail?.paymentMethod ?? '',
      code: detail?.payment?.code ?? detail?.paymentMethod ?? '',
      validFrom: detail?.payment?.validFrom,
      validTo: detail?.payment?.validTo,
      expired_time: detail?.payment?.expired_time,
    },
    orderState: {
      value:
        `${detail?.orderStatus ?? ''}`.toLowerCase() === 'success'
          ? 'success'
          : detail?.status ?? detail?.paymentStatus ?? 'paymentPending',
    },
    businessPartner: detail?.businessPartner ?? detail?.saleContext,
    expiredAt,
    startTime: orderDate,
    items: [checkoutItem],
    giftItems: [],
  } as any;
};

export const useCheckout = () => {
  useCheckoutSlice();
  const [listCart, setListCart] = useState<I_CheckoutCartItem[]>([]);
  const [vouchers, setVouchers] = useState<Partial<I_CheckoutCartItem>[]>([]);
  const [groupCart, setGroupCart] = useState<GroupDatas[]>([]);
  const { onOpenDialogNoti, onCloseDialogNoti } = useGlobalFunction();
  const translations = useSelector(selectTranslations);
  const { id }: { id: string } = useParams();

  const [loading, setLoading] = useState(false);
  const [enableConfirm, setEnableConfirm] = useState(false);
  const [idOrder, setIdOrder] = useState<string | number>();
  const [payment, setPayment] =
    useState<{ id: number; name: string; code?: string }>();
  const [paymentOptions, setPaymentOptions] = useState<any[]>([]);
  const orderCheckout = useRef<CheckoutDetail>();
  const [bankInfo, setBankInfo] = useState<any>();
  const [orderState, setOrderState] = useState<any>();
  const [expiredHours, setExpiredHours] = useState<any>();
  const [expiredAt, setExpiredAt] = useState<any>();
  const [startTime, setStartTime] = useState<any>();
  const [order, setOrder] = useState<any>();
  const [isValidPaymentTime, setIsValidPaymentTime] = useState<any>();
  const isSubmittingRef = useRef(false);

  const history = useHistory();

  const infoUser = useSelector(selectInfoUser);
  const listPaymentMethod = useSelector(selectListPaymentMethod);
  // const datesSelectedToCheckout = useSelector(selectDatesSelectedToCheckout);
  const INIT_FORM = useMemo(
    () => ({
      name: infoUser?.name || '',
      phone: infoUser?.phone || '',
      email: infoUser?.email || '',
    }),
    [infoUser],
  );

  const totalAmount = useMemo(
    () => listCart.reduce((result, item) => result + +item.amount, 0),
    [listCart],
  );

  const [totalPrice, setTotalPrice] = useState(0);

  const [totalPriceOriginal, setTotalPriceOriginal] = useState(0);
  const [totalDiscount, setTotalDiscount] = useState(0);

  useEffect(() => {
    const fetchPaymentOptions = async () => {
      try {
        const { data } = await getLOVPaymentMethodReservation();
        setPaymentOptions(Array.isArray(data) ? data : []);
      } catch {
        setPaymentOptions([]);
      }
    };

    fetchPaymentOptions();
  }, []);

  const getMappedPayment = useCallback(
    (paymentInfo?: { id: number; name: string; code?: string }) => {
      if (!paymentInfo) return paymentInfo;
      const paymentValue = getFirstValue(
        paymentInfo.code,
        paymentInfo.name,
        paymentInfo.id,
      );
      const paymentName = getPaymentMethodLabel(paymentValue, paymentOptions);

      return {
        ...paymentInfo,
        name: paymentName || paymentInfo.name,
      };
    },
    [paymentOptions],
  );

  const redirectToReservationResult = useCallback(() => {
    const reservationNo = orderCheckout.current?.idHash ?? id;
    if (reservationNo) {
      history.replace(`/restaurants/result/${reservationNo}`);
    }
  }, [history, id]);

  const onSubmitForm = useCallback(async () => {
    if (isSubmittingRef.current) return;

    try {
      isSubmittingRef.current = true;
      setLoading(true);
      const currentOrder = orderCheckout.current as any;
      const payload = {
        orderId: currentOrder?.orderId,
      };

      const { data } = await paymentReservationBookingApi(
        payload as any,
        {
          ignoreSuccessToast: true,
        } as any,
      );
      const checkoutErrorMessage = getCheckoutErrorMessage(data);
      if (data?.isSuccess === false && checkoutErrorMessage) {
        openMessageWithIcon('error', checkoutErrorMessage);
        redirectToReservationResult();
        return;
      }

      const qrCode = data?.ncbInfo?.qrCode;
      const qrContent = qrCode?.viet_qr_content || data.paymentUrl;
      const isNcbQrPayment =
        payment?.code?.toLowerCase() === 'ncb' || !!qrCode?.viet_qr_content;

      if (qrContent) {
        if (isNcbQrPayment) {
          history.push('/restaurants/ncb-payment', {
            id: orderCheckout.current?.idHash,
            qrImage: qrContent,
            bankInfo: {
              bankName: qrCode?.bankName ?? data?.ncbInfo?.bankName ?? '',
              bankAccount:
                qrCode?.account_no ?? data?.ncbInfo?.account_no ?? '',
              accountName:
                qrCode?.account_name ?? data?.ncbInfo?.account_name ?? '',
            },
          });
        } else {
          window.open(qrContent, '_self');
        }
      } else {
        if (data?.paymentSuccess) {
          // sessionStorage.removeItem(TYPE_LOCAL_STORAGE.ORDER);
          windowOpenLink({
            url: `/restaurants/payment-callback?vnp_TxnRef=${orderCheckout.current?.idHash}&bnPaymentType=Deposit&result_code=1&paymentStatus=${ReservationHistory_PaymentStatus.COMPLETED}`,
            target: '_self',
          });
        }
        if (data?.isPendingConfirm) {
          history.push(`/restaurants/result/${orderCheckout.current?.idHash}`);
          return;
        }

        if (data?.paymentStatus === ReservationHistory_PaymentStatus.COMPLETED && data?.status === ReservationHistoryStatus.CONFIRMED) {
          // sessionStorage.removeItem(TYPE_LOCAL_STORAGE.ORDER);
          windowOpenLink({
            url: `/restaurants/payment-callback?vnp_TxnRef=${orderCheckout.current?.idHash}&bnPaymentType=Deposit&result_code=1&paymentStatus=${ReservationHistory_PaymentStatus.COMPLETED}`,
            target: '_self',
          });
        } else if (data?.paymentStatus === ReservationHistory_PaymentStatus.FAILED && data?.status === ReservationHistoryStatus.CANCELLED) {
          windowOpenLink({
            url: `/restaurants/payment-callback?vnp_TxnRef=${orderCheckout.current?.idHash}&bnPaymentType=Deposit&result_code=0`,
            target: '_self',
          });
        }
      }
    } catch (err: any) {
      if (err?.action?.toLowerCase() === 'popup') {
        onOpenDialogNoti({
          type: 'ERROR',
          isShow: true,
          title: '',
          content: err?.message,
          buttons: [],
          maskClosable: false,
          closable: true,
          onCloseDialog: () => {
            onCloseDialogNoti();
            err?.data?.code === 'MSG070' &&
              history.replace('/account?route=tab-3');
          },
        });
        return;
      }

      const checkoutErrorMessage = getCheckoutErrorMessage(err);
      if (checkoutErrorMessage) {
        openMessageWithIcon('error', checkoutErrorMessage);
        redirectToReservationResult();
      }
    } finally {
      isSubmittingRef.current = false;
      setLoading(false);
    }
  }, [
    history,
    id,
    idOrder,
    onCloseDialogNoti,
    onOpenDialogNoti,
    payment?.code,
    redirectToReservationResult,
    translations,
  ]);
  const getTimeRemainingLabel = (targetTime: Orderdate, startOfTime) => {
    const expiredAt = moment(
      targetTime?.datetimeDMYHIS ?? undefined,
      'HH:mm:ss DD/MM/YYYY',
    );
    const now = moment();
    const diffMsFromNow = expiredAt.valueOf() - now.valueOf();
    if (diffMsFromNow <= 0) return undefined;
    const startTime = moment(
      startOfTime?.datetimeDMYHIS ?? undefined,
      'HH:mm:ss DD/MM/YYYY',
    );
    const time = expiredAt.valueOf() - startTime.valueOf();
    // Quy đổi ra giờ (bao gồm phần lẻ)
    const diffHours = time / (1000 * 60 * 60);
    // Làm tròn 1 chữ số thập phân (ví dụ: 1.3h)
    let roundedHours = Math.round(diffHours * 10) / 10;
    if (diffHours < 3 / 60) {
      roundedHours = Number(diffHours.toFixed(2));
    }
    return roundedHours;
  };

  const getOrderDetail = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getReservationBookingDetailApi({
        reservationNo: id,
      });
      if (!hasReservationDetailData(response.data)) {
        history.push('/page-not-found');
        throw new Error('Reservation detail not found');
      }
      const detailOrder = mapReservationDetailToCheckout(response.data, id);
      orderCheckout.current = detailOrder;
      setTotalDiscount(detailOrder.totalDiscount);
      setTotalPrice(detailOrder.totalPrice);
      setTotalPriceOriginal(detailOrder.totalOriginalPrice);
      setIdOrder(detailOrder.id);
      setPayment(getMappedPayment(detailOrder.payment));
      setBankInfo(detailOrder?.businessPartner?.bankInfo);
      setOrderState(detailOrder?.orderState);
      setOrder(detailOrder);
      const expiredHours = getTimeRemainingLabel(
        detailOrder?.expiredAt,
        detailOrder?.startTime,
      );
      setExpiredHours(expiredHours);

      setExpiredAt(detailOrder.expiredAt);
      const items: I_CheckoutCartItem[] = detailOrder?.items.map(item => ({
        ...item,
        product: {
          id: item.id,
          name: item.productName,
          price: item.price,
          originalPrice: item.originalPrice,
          categories: [] as I_Category[],
          amount: `${item.amount}`,
          parks: [item.park],
          park: item.park,
          image: item.park.image,
          versionB2b: item?.product?.versionB2b,
          usedArea2Object: item?.product?.usedArea2Object,
        },
        amount: `${item.amount}`,
        date: item.date.dateDMY,
        zone: item.zone,
        show: item.show,
        price: item.price,
        originalPrice: item.originalPrice,
        performance: { ...item.performance, show: item.show },
        park: item.park,
        catalog: item.catalog,
        isGift: false,
        selectedDate: item.selectedDate?.dateDMY ?? '',
      }));
      const giftItems: I_CheckoutCartItem[] =
        detailOrder?.giftItems
          ?.filter(
            val =>
              val.materialType === MATERRIAL_SHOW_TYPE ||
              val.materialType === MATERRIAL_PARK_TYPE,
          )
          .map(item => ({
            ...item,
            product: {
              id: item.id,
              name: item.productName ?? '',
              price: item.price,
              originalPrice: item.originalPrice,
              categories: [] as I_Category[],
              amount: `${item.amount}`,
              parks: [item.park],
              park: item.park,
              image: item.park?.image ?? '',
              versionB2b: item?.product?.versionB2b,
              usedArea2Object: item?.product?.usedArea2Object,
            },
            amount: `${item.amount ?? 0}`,
            date: item.date?.dateDMY ?? '',
            zone: item.zone,
            show: item.show,
            price: item.price,
            originalPrice: item.originalPrice,
            performance: { ...item.performance, show: item.show },
            park: item.park,
            catalog: item.catalog,
            isGift: true,
            selectedDate: item.selectedDate?.dateDMY ?? '',
            higherLevelItem: item.higherLevelItem ?? '',
          })) ?? [];
      const vouchersData: Partial<I_CheckoutCartItem>[] | any =
        detailOrder?.giftItems
          ?.filter(val => val.materialType === MATERRIAL_EVOUCHER_TYPE)
          .map(item => ({
            ...item,
            product: {
              id: item.id,
              name: item.productName ?? '',
              price: item.price,
              originalPrice: item.originalPrice,
              categories: [] as I_Category[],
              amount: `${item.amount}`,
              parks: [item.park],
              park: item.park,
              image: item.park?.image ?? '',
              versionB2b: item?.product?.versionB2b,
              usedArea2Object: item?.product?.usedArea2Object,
            },
            amount: `${item.amount ?? 0}`,
            date: item.date?.dateDMY ?? '',
            zone: item.zone,
            show: item.show,
            price: item.price,
            originalPrice: item.originalPrice,
            performance: { ...item.performance, show: item.show },
            park: item.park,
            catalog: item.catalog,
            isGift: true,
          })) ?? [];
      const groupDatas = Object.values(groupItems([...items, ...giftItems]));
      setVouchers(vouchersData);
      setGroupCart(groupDatas);
      setListCart([...items, ...giftItems]);
      const hourFrom = detailOrder?.payment?.validFrom;
      const hourTo = detailOrder?.payment?.validTo;
      const isValidPayment = isTimeValid(hourFrom, hourTo);
      setIsValidPaymentTime(
        !detailOrder?.payment?.validFrom && !detailOrder?.payment?.validTo
          ? true
          : isValidPayment,
      );
      return detailOrder;
    } catch (e) {
      history.push('/page-not-found');
      throw e;
    } finally {
      setLoading(false);
    }
  }, [getMappedPayment, history, id]);

  useEffect(() => {
    const getDetail = async () => {
      try {
        setLoading(true);
        // if (!getSessionItem(TYPE_LOCAL_STORAGE.ORDER)) {
        //   history.push('/page-not-found');
        //   return;
        // }
        setEnableConfirm(true);
        // const id = JSON.parse(getSessionItem(TYPE_LOCAL_STORAGE.ORDER) ?? '');
        const response = await getReservationBookingDetailApi({
          reservationNo: id,
        });
        if (!hasReservationDetailData(response.data)) {
          history.push('/page-not-found');
          return;
        }
        const detailOrder = mapReservationDetailToCheckout(response.data, id);
        orderCheckout.current = detailOrder;
        setTotalDiscount(detailOrder.totalDiscount);
        setTotalPrice(detailOrder.totalPrice);
        setTotalPriceOriginal(detailOrder.totalOriginalPrice);
        setIdOrder(detailOrder.id);
        setPayment(getMappedPayment(detailOrder.payment));
        setBankInfo(detailOrder?.businessPartner?.bankInfo);
        setOrderState(detailOrder?.orderState);
        setOrder(detailOrder);
        const expiredHours = getTimeRemainingLabel(
          detailOrder?.expiredAt,
          detailOrder?.startTime,
        );
        setExpiredHours(expiredHours);

        setExpiredAt(detailOrder.expiredAt);
        setStartTime(detailOrder.startTime);
        const items: I_CheckoutCartItem[] = detailOrder?.items.map(item => ({
          ...item,
          product: {
            id: item.id,
            name: item.productName,
            price: item.price,
            originalPrice: item.originalPrice,
            categories: [] as I_Category[],
            amount: `${item.amount}`,
            parks: [item.park],
            park: item.park,
            image: item.park.image,
            versionB2b: item?.product?.versionB2b,
            usedArea2Object: item?.product?.usedArea2Object,
          },
          amount: `${item.amount}`,
          date: item.date.dateDMY,
          zone: item.zone,
          show: item.show,
          price: item.price,
          originalPrice: item.originalPrice,
          performance: { ...item.performance, show: item.show },
          park: item.park,
          catalog: item.catalog,
          isGift: false,
          selectedDate: item.selectedDate?.dateDMY ?? '',
        }));
        const giftItems: I_CheckoutCartItem[] =
          detailOrder?.giftItems
            ?.filter(
              val =>
                val.materialType === MATERRIAL_SHOW_TYPE ||
                val.materialType === MATERRIAL_PARK_TYPE,
            )
            .map(item => ({
              ...item,
              product: {
                id: item.id,
                name: item.productName ?? '',
                price: item.price,
                originalPrice: item.originalPrice,
                categories: [] as I_Category[],
                amount: `${item.amount}`,
                parks: [item.park],
                park: item.park,
                image: item.park?.image ?? '',
                versionB2b: item?.product?.versionB2b,
                usedArea2Object: item?.product?.usedArea2Object,
              },
              amount: `${item.amount ?? 0}`,
              date: item.date?.dateDMY ?? '',
              zone: item.zone,
              show: item.show,
              price: item.price,
              originalPrice: item.originalPrice,
              performance: { ...item.performance, show: item.show },
              park: item.park,
              catalog: item.catalog,
              isGift: true,
              selectedDate: item.selectedDate?.dateDMY ?? '',
              higherLevelItem: item.higherLevelItem ?? '',
            })) ?? [];
        const vouchersData: Partial<I_CheckoutCartItem>[] | any =
          detailOrder?.giftItems
            ?.filter(val => val.materialType === MATERRIAL_EVOUCHER_TYPE)
            .map(item => ({
              ...item,
              product: {
                id: item.id,
                name: item.productName ?? '',
                price: item.price,
                originalPrice: item.originalPrice,
                categories: [] as I_Category[],
                amount: `${item.amount}`,
                parks: [item.park],
                park: item.park,
                image: item.park?.image ?? '',
                versionB2b: item?.product?.versionB2b,
                usedArea2Object: item?.product?.usedArea2Object,
              },
              amount: `${item.amount ?? 0}`,
              date: item.date?.dateDMY ?? '',
              zone: item.zone,
              show: item.show,
              price: item.price,
              originalPrice: item.originalPrice,
              performance: { ...item.performance, show: item.show },
              park: item.park,
              catalog: item.catalog,
              isGift: true,
            })) ?? [];
        const groupDatas = Object.values(groupItems([...items, ...giftItems]));
        setVouchers(vouchersData);
        setGroupCart(groupDatas);
        setListCart([...items, ...giftItems]);
        const hourFrom = detailOrder?.payment?.validFrom;
        const hourTo = detailOrder?.payment?.validTo;
        const isValidPayment = isTimeValid(hourFrom, hourTo);
        setIsValidPaymentTime(
          !detailOrder?.payment?.validFrom && !detailOrder?.payment?.validTo
            ? true
            : isValidPayment,
        );
        // sessionStorage.removeItem(TYPE_LOCAL_STORAGE.ORDER);
      } catch (e) {
        history.push('/page-not-found');
        throw e;
      } finally {
        setLoading(false);
      }
    };
    getDetail();
  }, [getMappedPayment, history, id]);

  return useMemo(
    () => ({
      loading,
      INIT_FORM,
      onSubmitForm,
      listPaymentMethod,
      // listCartSelectedToCheckout,
      totalAmount,
      totalPrice,
      totalPriceOriginal,
      orderCheckout,
      enableConfirm,
      setEnableConfirm,
      totalDiscount,
      groupCart,
      infoUser,
      setLoading,
      vouchers,
      listCart,
      idOrder,
      payment,
      bankInfo,
      orderState,
      expiredHours,
      isValidPaymentTime,
      expiredAt,
      startTime,
      order,
      getOrderDetail,
    }),
    [
      loading,
      INIT_FORM,
      onSubmitForm,
      listPaymentMethod,
      // listCartSelectedToCheckout,
      totalAmount,
      totalPrice,
      totalPriceOriginal,
      orderCheckout,
      enableConfirm,
      setEnableConfirm,
      totalDiscount,
      groupCart,
      infoUser,
      setLoading,
      vouchers,
      listCart,
      idOrder,
      payment,
      bankInfo,
      orderState,
      expiredHours,
      isValidPaymentTime,
      expiredAt,
      startTime,
      order,
      getOrderDetail,
    ],
  );
};
